import { z, type ZodType } from '@customerio/hydra-validation';
import type {
  ActionToUI as UIAPIActionToUI,
  CampaignsSubjectResponse,
  Subject as UIAPISubject,
  WebhookResponse as UIAPIWebhookResponse,
} from '@customerio/hydra-ui-api';
import { defineEntity } from '../shared/define-entity';

/**
 * Subject entity — a person's journey through a campaign (Ember `subject`
 * model). Read-only here: the delivery detail links to the full journey and
 * surfaces the journey's status/next-step plus a webhook step's response.
 *
 * Timestamps (`match_time`, `next_wake_up`) stay as raw epoch-second numbers,
 * like the Delivery entity — Pluma's date components consume epoch seconds
 * directly.
 */

export type Subject = UIAPISubject;
export type SubjectAction = UIAPIActionToUI;
export type SubjectWebhookResponse = UIAPIWebhookResponse;

/**
 * The subject-detail response (`GET /v1/environments/:id/subjects/:id`): the
 * subject plus its sideloaded `subject_steps`, `actions`, and `campaigns`. Kept
 * as the generated wire shape (snake_case) so contract changes stay
 * type-checked; only `subject` is swapped for the parsed entity. `subject_steps`
 * carry per-delivery webhook responses; `actions` resolve the subject's
 * `next_action_id`; `campaigns` tell webhook-triggered journeys from person ones.
 */
export type SubjectDetail = Omit<CampaignsSubjectResponse, 'subject'> & { subject: Subject };

/**
 * We trust the generated `Subject` wire shape (read-only passthrough) and only
 * validate the pk. `z.custom<Subject>` states that intent directly — the parsed
 * value is a `Subject` — instead of asserting it, and the refinement keeps the
 * `id: string` guard the entity's `pk` relies on.
 */
const hasStringId = z.object({ id: z.string() });
const SubjectFromApi: ZodType<Subject, unknown> = z.custom<Subject>((value) => hasStringId.safeParse(value).success);

/** @public */
export const subject = defineEntity<Subject, Subject, void, undefined, 'workspace'>({
  name: 'subject',
  scope: 'workspace',
  pk: (s) => s.id,
  schema: { read: SubjectFromApi },
});

export const subjectKeys = subject.keys;

export function parseSubject(raw: UIAPISubject): Subject {
  return subject.parse(raw);
}
